import Person from "@/types/Person.interface"
import TenantMembership from "@/types/TenantMembership.interface"
import User, { UserMetric, UserRole } from "@/types/User.interface"
import { store } from ".."

interface State {
    users: User[]
    usersCreate: boolean
    usersMetrics: UserMetric[]
    userCount: number
}

const defaults = () => {
    return {
        userCount: 0,
        usersCreate: false,
        usersMetrics: null,
        users: []
    }
}

export default {
    state: defaults(),
    mutations: {
        userCountSet (state: State, count: number): void {
            state.userCount = count
        },
        usersCreateSet (state: State, bool: boolean): void {
            state.usersCreate = bool
        },
        usersPush (state: State, users: User[]): void {
            state.users = window.removeDuplicatesById (state.users, users)

            //  Publish the usersPushedEvent
            // events.onUsersPushed()
        },
        usersRemove (state: State, ids: number[]): void {
            //  Get the globa list of users
            const users = state.users

            //  If no users, do nothing
            if (!users) return

            //  Else, filter our out removed ids
            const filtered = users.filter((x: User) => !ids.includes(x.id))

            //  Replace our list with the filtered list
            state.users = filtered
        },
        usersMetricsSet (state: State, metrics: UserMetric[]): void {
            state.usersMetrics = metrics
        },
        usersSet (state: State, users: User[]): void {
            state.users = users
        },
        usersReset (state: State): void {
            Object.assign (state, defaults())
        }
    },
    actions: {
        usersDelete ({ commit, getters }, userId: number): Promise<User> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users/${userId}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                //  Remove it from our lists
                commit('usersRemove', [response.data.id])

                //  Remove it from the users view as well
                commit('teammatesUsersRemove', [response.data.id])

                //  Return the deleted user
                return response.data
            }).catch(() => null)
        },
        usersGet ({ commit, getters }): Promise<User> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our global users list
                commit('usersSet', response.data)

                //  Set our users view list
                commit('teammatesUsersSet', response.data)

                //  Return the users list
                return response.data
            }).catch(() => null)
        },
        usersFind ({ getters }, userId: number): Promise<User> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users/${userId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return the found user
                return response.data
            }).catch(() => null)
        },
        usersLogoUpload ({ commit, getters }, payload: FormData): Promise<User> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users/logo`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Push this user into our global list
                commit('usersPush', [response.data])

                //  Update our users view list too
                commit('teammatesUsersPush', [response.data])

                //  GRab the id from this user
                const id = response.data.id

                //  Check if it matches the auth user
                const sameUser = id === getters.user.id

                //  If same user, update our auth user
                if (sameUser) commit('authUserSet', response.data)

                //  Return the updated user
                return response.data
            }).catch(() => null)
        },
        usersMetrics ({ commit, getters }, payload): Promise<UserMetric[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users/metrics`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Update our metrics state
                commit('usersMetricsSet', response.data)

                //  Return our data
                return response.data
            }).catch(() => null)
        },
        usersPasswordReset ({ commit, getters }, user: User): Promise<User> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users/password`
            
            //  Send the request
            return window.axios.put(url, user).then(response => {
                //  Push this user into our global list
                commit('usersPush', [response.data])

                //  Update our users view list too
                commit('teammatesUsersPush', [response.data])

                //  GRab the id from this user
                const id = response.data.id

                //  Check if it matches the auth user
                const sameUser = id === getters.user.id

                //  If same user, update our auth user
                if (sameUser) commit('authUserSet', response.data)

                //  Return the updated user
                return response.data
            }).catch(() => null)
        },
        usersPasswordResetSend ({ getters }, userId: number): Promise<string> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users/password/reset-send`

            //  Construct our payload
            const payload = { id: userId }

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return the response from the API
                return response.data
            }).catch(() => null)
        },
        usersPersonFind({ getters }, userId: number) {
            //  Get the global persons list
            const persons = getters.persons

            //  Search for the exsiting user in the global dataset
            const existingPerson = persons.find((x: Person) => x.user_id == userId)

            //  If we found an existing person, return it
            if (existingPerson) return existingPerson

            //  Else, assemble the API endpoint
            const url = `${getters.api}/users/${userId}/person`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return the found person
                return response.data
            }).catch(() => null)
        },
        usersPut ({ commit, getters }, user: User): Promise<User> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users`

            //  Send the request
            return window.axios.put(url, user).then(response => {
                //  Push this user into our global list
                commit('usersPush', [response.data])

                //  Update our users view list too
                commit('teammatesUsersPush', [response.data])

                //  GRab the id from this user
                const id = response.data.id

                //  Check if it matches the auth user
                const sameUser = id === getters.user.id

                //  If same user, update our auth user
                if (sameUser) commit('authUserSet', response.data)

                //  Return the updated user
                return response.data
            }).catch(() => null)
        },
        usersPost ({ commit, getters }, user: User): Promise<void> {
            //  Assemble the API endpoint
            const url = `${getters.api}/users`

            //  Send the request
            return window.axios.post(url, user).then(response => {
                //  Push this user into our global list
                commit('usersPush', [response.data])

                //  Update our users view list too
                commit('teammatesUsersPush', [response.data])

                //  Return the newly created user
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        adminUser (state, getters): User {
            //  Grab our tenant
            const tenant = getters.tenant

            //  If no tenant, return null
            if (!tenant) return null

            //  Grab all of our users
            const users = state.users

            //  Grab the admin id from our tenant
            const adminId = tenant.admin_user_id

            //  Find our admin user
            const admin = users.find((x: User) => x.id === adminId)

            //  Return our admin user
            return admin
        },
        //  Check if the inputed user is the admin of the current selected tenant
        userIsAdmin: (_, getters) => (user: User): boolean => {
            //  Get the current tenant
            const tenant = getters.tenant

            //  If the tenant does NOT exist, return error
            if (!tenant) throw 'No tenant is currently selected'

            //  Check if the user id is same a tenant's admin id
            const isAdmin = user.id === getters.tenant.admin_user_id

            //  Return bool result
            return isAdmin
        },
        //  Check if the current authenticated user is the same as inputed user
        userIsSelf: (_, getters) => (user: User): boolean => {
            //  Fetch the current authenticated user
            const authenticated = getters.user

            //  If no auth was found, return error
            if (!authenticated) throw 'No authenticated user found'

            //  Check if the user is the same as authenticated user
            const isSelf = authenticated.id === user.id

            //  Return bool result
            return isSelf
        },
        //  Retrieve the tenant membership for the inputted user
        userMembership: (st_ate, getters) => (user: User): TenantMembership => {
            //  Fetch the currently selected tenant
            const tenant = getters.tenant

            //  If the tenant does NOT exist, return error
            if (!tenant) throw 'No tenant is currently selected'

            //  Find the user's tenant memberships
            const membership = user.memberships.find(x => x.tenant_id === tenant.id)

            //  Return the membership
            return membership
        },
        userOutranks: (_, getters) => (permission: number): boolean => {
            //  Fetch the currently authenticated user
            const user = getters.user

            //  If the user does NOT exist, return nothing
            if (!user) return null

            //  Get the current user's tenant role
            const role = getters.userRole(user)

            //  If the role does NOT exist, return nothing
            if (!role) return null

            //  Check the current user outranks the permission input
            const outranks = role.permission > permission

            //  Return the bool result
            return outranks
        },
        userOutranksUser: (_, getters) => (user: User): boolean => {
            //  If user is null, return true
            if (!user) return true

            //  Fetch the current authenticated user
            const authenticated = getters.user

            //  If no user exists, return error
            if (!authenticated) throw 'No authenticated user exists'

            //  Check if the user is the authenticated user
            const isSelf = authenticated.id === user.id

            //  If the user is the current user, return true
            if (isSelf) return true

            //  Check if current auth permission outranks user permission
            const myRole = getters.userRole(getters.user)
            const theirRole = getters.userRole(user)

            //  Role can be null if the user has been removed from the tenant but messages still exist for them
            if (!theirRole) return true

            const outranks = myRole.permission > theirRole.permission

            //  Return the bool result
            return outranks
        },
        userRole: (_, getters) => (user: User): UserRole => {
            //  If no user exists, return null
            if (!user) return null
            
            //  If there is no tenant, return null
            if (!getters.tenant) return null

            //  If there are no roles, return null
            if (!user.roles.length) return null

            //  We can get the role from the roles property
            const role = user.roles.find(role => {
                return role.pivot.tenant_id === getters.tenant.id
            })

            //  Return the name of their role
            return role
        },
        userTenantMembership (_, getters): TenantMembership {
            //  If no user, return null
            if (!getters.user) return null

            //  Else, find the right membership
            return getters.user.memberships.find((m: TenantMembership) => m.tenant_id === getters.tenant.id)
        },
        userCount (state: State): number {
            return state.userCount
        },
        usersCreate (state: State): boolean {
            return state.usersCreate
        },
        usersMetrics (state: State): UserMetric[] {
            return state.usersMetrics
        },
        users (state: State): User[] {
            return state.users
        }
    }
}
